import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Checkbox,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import PropTypes from 'prop-types';
import Iconify from '../../../../reusable-components/iconify';
import Lightbox from '../../../../reusable-components/lightbox';
import { cloudinaryThumbnail, cloudinaryFull } from '../../../../utils/formatCloudinary';
import { suggestCatalogCombinations, buildCombineSuggestionPayload } from '../../../../api/catalog';

Step3.propTypes = {
  parentData: PropTypes.object,
  productData: PropTypes.object,
  setProductData: PropTypes.func,
  categories: PropTypes.array,
  vendor: PropTypes.object,
};

export default function Step3({
  parentData = null,
  productData = null,
  setProductData,
  categories = [],
  vendor = null,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [lightboxImage, setLightboxImage] = useState('');

  useEffect(() => {
    let active = true;

    const fetchSuggestions = async () => {
      if (!parentData?.description) {
        setOptions([]);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const newItem = buildCombineSuggestionPayload(parentData, productData, categories);
        const response = await suggestCatalogCombinations(newItem);
        if (!active) return;
        setOptions(response.status === 200 && Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        if (active) {
          setOptions([]);
          setError('Failed to load combination suggestions');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchSuggestions();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) =>
      `${opt.description} ${opt.productId} ${opt.category || ''} ${opt.manufacturerIds || ''}`
        .toLowerCase()
        .includes(term)
    );
  }, [options, search]);

  const selectedId = productData?.groupto || '';

  const handleToggle = (option) => {
    setProductData((prev) => {
      const isUnselecting = prev?.groupto === option.productId;
      return {
        ...prev,
        groupto: isUnselecting ? '' : option.productId,
        grouptoDescription: isUnselecting ? '' : option.description,
      };
    });
  };

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>
        Combine Item
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        These existing catalog items closely match{' '}
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          &ldquo;{parentData?.description}&rdquo;
        </Box>
        {vendor?.label ? (
          <>
            {' '}from{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {vendor.label}
            </Box>
          </>
        ) : null}
        . Combine with one instead of creating a duplicate PS ID, or continue without combining
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search suggested products..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoComplete="off"
        sx={{ mt: 2 }}
        InputProps={{
          startAdornment: (
            <Iconify icon="eva:search-fill" width={20} sx={{ mr: 1, color: 'text.disabled' }} />
          ),
        }}
      />

      <Box sx={{ mt: 2, maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && error && (
          <Typography variant="body2" color="error" sx={{ py: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        {!loading && !error && filteredOptions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {options.length === 0 ? 'No similar items found' : 'No matching products found'}
          </Typography>
        )}

        <Stack spacing={1}>
          {!loading &&
            filteredOptions.map((opt) => {
              const checked = selectedId === opt.productId;
              return (
                <Box
                  key={opt.productId}
                  onClick={() => handleToggle(opt)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: (theme) =>
                      `1px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
                    bgcolor: checked ? '#eef7e2' : 'background.paper',
                    '&:hover': { bgcolor: checked ? '#eef7e2' : 'action.hover' },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'grey.200',
                    }}
                  >
                    {opt.pictureUrl && (
                      <Box
                        component="img"
                        src={cloudinaryThumbnail(opt.pictureUrl)}
                        alt=""
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightboxImage(cloudinaryFull(opt.pictureUrl));
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {opt.description}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: 'block' }}
                    >
                      ID {opt.productId}
                      {opt.category ? ` · ${opt.category}` : ''}
                      {opt.manufacturerIds ? ` · MFG ${opt.manufacturerIds}` : ''}
                    </Typography>
                  </Box>
                  <Checkbox
                    checked={checked}
                    color="primary"
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggle(opt)}
                  />
                </Box>
              );
            })}
        </Stack>
      </Box>

      <Lightbox
        slides={lightboxImage ? [{ src: lightboxImage }] : []}
        open={Boolean(lightboxImage)}
        close={() => setLightboxImage('')}
      />
    </>
  );
}
